import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage synchronously to avoid race condition
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('edible-cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  
  // Track if this is the initial mount to avoid overwriting localStorage
  const isInitialMount = useRef(true);

  // Save cart to localStorage whenever it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem('edible-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, customization = null) => {
    // Normalize product ID (handle both _id and id)
    const productId = product.id || product._id;
    const normalizedProduct = { ...product, id: productId };
    
    // If customization is not passed separately, use the one from product
    const finalCustomization = customization || product.customization || null;
    
    console.log('[Cart] Adding to cart:', {
      productId,
      productName: product.name,
      hasCustomization: !!finalCustomization,
      customizationKeys: finalCustomization ? Object.keys(finalCustomization) : []
    });
    
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => 
        item.id === productId && 
        JSON.stringify(item.customization) === JSON.stringify(finalCustomization)
      );

      if (existingItem) {
        return prevItems.map(item =>
          item.id === productId && 
          JSON.stringify(item.customization) === JSON.stringify(finalCustomization)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [...prevItems, { ...normalizedProduct, quantity: 1, customization: finalCustomization }];
    });
  };

  const removeFromCart = (productId, customization = null) => {
    setCartItems(prevItems =>
      prevItems.filter(
        item => !(item.id === productId && 
        JSON.stringify(item.customization) === JSON.stringify(customization))
      )
    );
  };

  const updateQuantity = (productId, quantity, customization = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, customization);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId && 
        JSON.stringify(item.customization) === JSON.stringify(customization)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
